import { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'

import { UPDATE_KANBAN_WORKFLOW } from '../../../gql/mutations/kanban'
import { GET_ASSIGNMENT_USERS_LIST } from '../../../gql/queries/usersQuery'
import { IAssignmentGroupSimple } from '../../../types/assignmentGroups'
import { IKanbanWorkflow, IUpdateKanbanWorkflowRes } from '../../../types/kanban'
import { parseId } from '../../../helpers'

interface Props {
  show: boolean
  workflow?: IKanbanWorkflow
  availableGroups: IAssignmentGroupSimple[]
  onClose: () => void
  onUpdated: (workflow?: IKanbanWorkflow) => void
  canSelectMultipleGroups?: boolean
  defaultGroupId?: string | null
  userGroups?: { id: string; name: string }[]
  allowedGroups?: IAssignmentGroupSimple[]
}

export const ModalEditKanbanWorkflow: React.FC<Props> = ({
  show,
  workflow,
  availableGroups,
  onClose,
  onUpdated,
  canSelectMultipleGroups = false,
  defaultGroupId,
  userGroups = [],
  allowedGroups,
}) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<'PRIVATE' | 'GROUP' | 'PUBLIC' | 'SHARED'>('GROUP')
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [sharedUserIds, setSharedUserIds] = useState<string[]>([])
  const [updateWorkflow, { loading }] = useMutation<IUpdateKanbanWorkflowRes>(UPDATE_KANBAN_WORKFLOW)
  const { data: usersData } = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    variables: { assignmentCandidates: true },
  })
  const allUsers = usersData?.allUsersList ?? []
  const userGroupMatches = useMemo(() => {
    if (!userGroups.length) return []
    const ids = new Set(userGroups.map((group) => group.id))
    return availableGroups.filter((group) => ids.has(group.id))
  }, [availableGroups, userGroups])
  const defaultGroupMatch = useMemo(() => {
    if (!defaultGroupId || defaultGroupId === 'all') return null
    return availableGroups.find((group) => group.id === defaultGroupId) ?? null
  }, [availableGroups, defaultGroupId])
  const singleGroup = useMemo(() => {
    if (canSelectMultipleGroups) return null
    return userGroupMatches[0] ?? defaultGroupMatch ?? availableGroups[0] ?? null
  }, [availableGroups, canSelectMultipleGroups, defaultGroupMatch, userGroupMatches])

  const multiSelectGroups = useMemo(
    () =>
      canSelectMultipleGroups
        ? allowedGroups && allowedGroups.length > 0
          ? allowedGroups
          : availableGroups
        : [],
    [allowedGroups, availableGroups, canSelectMultipleGroups]
  )

  useEffect(() => {
    if (show && workflow) {
      setName(workflow.name)
      setDescription(workflow.description ?? '')
      setVisibility((workflow.visibility as typeof visibility) || 'GROUP')
      const groups =
        workflow.groupAssignments?.edges
          ?.map((edge) => parseId(edge.node.id))
          .filter((value): value is string => Boolean(value)) ?? []
      setSelectedGroups(groups)
      const shared =
        workflow.sharedWith?.edges
          ?.map((edge) => parseId(edge.node.id))
          .filter((value): value is string => Boolean(value)) ?? []
      setSharedUserIds(shared)
    }
  }, [show, workflow])

  useEffect(() => {
    if (!canSelectMultipleGroups && visibility === 'GROUP' && selectedGroups.length === 0) {
      if (workflow?.groupAssignments?.edges?.length) {
        setSelectedGroups(workflow.groupAssignments.edges.map((edge) => edge.node.id))
      } else if (singleGroup?.id) {
        setSelectedGroups([singleGroup.id])
      }
    }
    if (!canSelectMultipleGroups && visibility !== 'GROUP' && selectedGroups.length) {
      setSelectedGroups([])
    }
  }, [
    canSelectMultipleGroups,
    selectedGroups.length,
    visibility,
    workflow?.groupAssignments?.edges,
    singleGroup?.id,
  ])

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleGroupToggle = (groupId: string) => {
    if (!canSelectMultipleGroups) return
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    )
  }

  const handleToggleSharedUser = (userId: string) => {
    setSharedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!workflow) return
    const groupAssignmentIds =
      visibility === 'GROUP'
        ? canSelectMultipleGroups
          ? selectedGroups
          : selectedGroups.length
            ? selectedGroups
            : singleGroup?.id
              ? [singleGroup.id]
              : []
        : null
    if (visibility === 'GROUP' && (!groupAssignmentIds || groupAssignmentIds.length === 0)) {
      toast.error('Select at least one assignment group')
      return
    }
    try {
      const { data } = await updateWorkflow({
        variables: {
          id: parseId(workflow.id),
          name,
          description: description || null,
          visibility,
          groupAssignmentIds: groupAssignmentIds ?? [],
          sharedUserIds: visibility === 'SHARED' ? sharedUserIds : [],
        },
      })
      if (!data?.updateKanbanWorkflow?.ok || !data.updateKanbanWorkflow.workflow) {
        throw new Error(
          data?.updateKanbanWorkflow?.errors?.[0] || 'Error updating the board'
        )
      }
      toast.success('Board updated successfully')
      onUpdated(data.updateKanbanWorkflow.workflow)
      onClose()
    } catch (error) {
      console.error(error)
      toast.error('Error updating the board')
    }
  }

  if (!workflow) return null

  return (
    <Modal show={show} onHide={handleClose} centered className='kanban-workflow-modal'>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className='kanban-workflow-modal__header'>
          <Modal.Title>Edit Board</Modal.Title>
        </Modal.Header>
        <Modal.Body className='kanban-workflow-modal__body'>
          <Form.Group className='kanban-workflow-modal__group'>
            <Form.Label>Name</Form.Label>
            <Form.Control
              className='kanban-workflow-modal__input'
              required
              value={name}
              maxLength={20}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className='kanban-workflow-modal__group'>
            <Form.Label>Description</Form.Label>
            <Form.Control
              className='kanban-workflow-modal__input'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group className='kanban-workflow-modal__group'>
            <Form.Label>Visibility</Form.Label>
            <Form.Select
              className='kanban-workflow-modal__input'
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as typeof visibility)}
            >
              <option value='PRIVATE'>Only me</option>
              <option value='GROUP'>My assignment group</option>
              <option value='SHARED'>Shared with specific users</option>
              <option value='PUBLIC'>Organization</option>
            </Form.Select>
            {!canSelectMultipleGroups && visibility === 'GROUP' && !singleGroup?.id && (
              <small className='text-muted'>
                No assignment group detected. Ask an administrator to add you to one.
              </small>
            )}
          </Form.Group>
          {visibility === 'GROUP' && (
            <Form.Group className='kanban-workflow-modal__group'>
              <Form.Label>Assign to groups</Form.Label>
              <div className='kanban-workflow-modal__groups'>
                {canSelectMultipleGroups ? (
                  multiSelectGroups.length === 0 ? (
                    <small>No groups available. Create one first.</small>
                  ) : (
                    multiSelectGroups.map((group) => (
                      <Form.Check
                        key={group.id}
                        type='checkbox'
                        id={`edit-workflow-group-${group.id}`}
                        className='kanban-workflow-modal__checkbox'
                        label={group.name}
                        checked={selectedGroups.includes(group.id)}
                        onChange={() => handleGroupToggle(group.id)}
                      />
                    ))
                  )
                ) : singleGroup?.name ? (
                  <div className='text-muted'>
                    <span className='badge bg-light text-dark me-2 mb-2'>{singleGroup.name}</span>
                  </div>
                ) : (
                  <small className='text-muted'>No assignment group detected for your user.</small>
                )}
              </div>
            </Form.Group>
          )}
          {visibility === 'SHARED' && (
            <Form.Group className='kanban-workflow-modal__group'>
              <Form.Label>Select users</Form.Label>
              <div className='kanban-workflow-modal__users' style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '4px' }}>
                {allUsers.length === 0 ? (
                  <div className='text-muted'>No users found.</div>
                ) : (
                  allUsers.map((user: any) => (
                    <Form.Check
                      key={user.id}
                      type='checkbox'
                      id={`edit-workflow-user-${user.id}`}
                      className='kanban-workflow-modal__checkbox'
                      label={`${user.firstName} ${user.lastName} (${user.email})`}
                      checked={sharedUserIds.includes(user.id)}
                      onChange={() => handleToggleSharedUser(user.id)}
                    />
                  ))
                )}
              </div>
              <div className='mt-2'>
                <small className='text-muted'>{sharedUserIds.length} users selected</small>
              </div>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className='kanban-workflow-modal__footer'>
          <Button variant='light' onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant='primary' type='submit' disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal >
  )
}
